import React, { useState, useEffect } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { isValidEmail, validatePassword, validateDisplayName } from '../utils/validation';
import '../styles/global.css';
import '../styles/authModal.css';

const AuthModal = ({ isOpen, onClose, initialTab = 'login' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState({
    login: { email: '', password: '' },
    register: { name: '', email: '', password: '', confirmPassword: '' }
  });
  const [formErrors, setFormErrors] = useState({
    login: { email: '', password: '' },
    register: { name: '', email: '', password: '', confirmPassword: '' }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const passwordRequirements = [
    { label: "At least 6 characters", test: pwd => pwd.length >= 6 },
    { label: "At least one uppercase letter", test: pwd => /[A-Z]/.test(pwd) },
    { label: "At least one lowercase letter", test: pwd => /[a-z]/.test(pwd) },
    { label: "At least one number", test: pwd => /[0-9]/.test(pwd) },
    { label: "At least one special character", test: pwd => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) }
  ];

  if (!isOpen) return null;

  const validateForm = (formType) => {
    let isValid = true;
    const newErrors = { ...formErrors };
    
    if (formType === 'login') {
      const { email, password } = formData.login;
      
      if (!email) {
        newErrors.login.email = 'Email is required';
        isValid = false;
      } else if (!isValidEmail(email)) {
        newErrors.login.email = 'Invalid email format';
        isValid = false;
      } else {
        newErrors.login.email = '';
      }
      
      if (!password) {
        newErrors.login.password = 'Password is required';
        isValid = false;
      } else {
        newErrors.login.password = '';
      }
    } else if (formType === 'register') {
      const { name, email, password, confirmPassword } = formData.register;
      
      if (!name || name.trim() === '') {
        newErrors.register.name = 'Name is required';
        isValid = false;
      } else if (name.length < 3) {
        newErrors.register.name = 'Name must be at least 3 characters';
        isValid = false;
      } else if (name.length > 50) {
        newErrors.register.name = 'Name must not exceed 50 characters';
        isValid = false;
      } else {
        newErrors.register.name = '';
      }
      
      if (!email) {
        newErrors.register.email = 'Email is required';
        isValid = false;
      } else if (!isValidEmail(email)) {
        newErrors.register.email = 'Invalid email format';
        isValid = false;
      } else {
        newErrors.register.email = '';
      }
      
      if (!password) {
        newErrors.register.password = 'Password is required';
        isValid = false;
      } else {
        const passwordResult = validatePassword(password);
        if (!passwordResult.valid) {
          newErrors.register.password = passwordResult.message;
          isValid = false;
        } else {
          newErrors.register.password = '';
        }
      }
      
      if (password !== confirmPassword) {
        newErrors.register.confirmPassword = 'Passwords do not match';
        isValid = false;
      } else {
        newErrors.register.confirmPassword = '';
      }
    }
    
    setFormErrors(newErrors);
    return isValid;
  };

  const handleChange = (e, formType) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [formType]: {
        ...formData[formType],
        [name]: value
      }
    });
    
    setFormErrors({
      ...formErrors,
      [formType]: {
        ...formErrors[formType],
        [name]: ''
      }
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm('login')) {
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      const { email, password } = formData.login;
      const userCred = await signInWithEmailAndPassword(auth, email, password);

      if (!userCred.user.emailVerified) {
        setError('Please verify your email before logging in.');
        setLoading(false);
        return;
      }

      onClose(); // success
    } catch (err) {
      console.error(err);
      
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later');
      } else {
        setError('An error occurred during login. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm('register')) {
      return;
    }
    
    setLoading(true);
    setError(null);

    const { name, email, password } = formData.register;

    try {
      const auth = getAuth();
      const userCred = await createUserWithEmailAndPassword(auth, email, password);

      if (!userCred || !userCred.user) {
        throw new Error('User creation failed');
      }

      await updateProfile(userCred.user, { displayName: name });
      await sendEmailVerification(userCred.user);

      await setDoc(doc(db, 'users', userCred.user.uid), {
        displayName: name,
        email: email,
        columnTitle: `${name}'s Column`,
        columnStyle: 'primary-font',
        bio: '',
        photoURL: userCred.user.photoURL || '',
        location: null,
        createdAt: new Date()
      });

      alert('Registration successful! Please check your email to verify your account.');
      onClose();
    } catch (err) {
      console.error(err);
      
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please try logging in.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Email/password registration is not allowed at this time.');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate password strength percentage
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;
    
    let strength = 0;
    for (const req of passwordRequirements) {
      if (req.test(password)) strength += 20; // 5 requirements = 20% each
    }
    
    return strength;
  };

  const getPasswordStrengthLabel = (strength) => {
    if (strength < 20) return 'Very Weak';
    if (strength < 40) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    if (strength < 100) return 'Strong';
    return 'Very Strong';
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 40) return '#ff4d4f';
    if (strength < 60) return '#faad14';
    if (strength < 80) return '#1890ff';
    return '#52c41a';
  };

  const passwordStrength = calculatePasswordStrength(formData.register.password);
  const strengthLabel = getPasswordStrengthLabel(passwordStrength);
  const strengthColor = getPasswordStrengthColor(passwordStrength);

  return (
    <div className="auth-modal" onClick={onClose}>
      <div className="auth-modal-content" onClick={e => e.stopPropagation()}>
        <div className="auth-tabs">
          <div className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`} onClick={() => setActiveTab('login')}>Login</div>
          <div className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`} onClick={() => setActiveTab('register')}>Register</div>
        </div>

        {error && (
          <div style={{ color: 'red', marginBottom: '1rem', background: '#ffeeee', padding: '0.5rem' }}>
            {error}
          </div>
        )}

        {activeTab === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="auth-form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                className={`auth-input ${formErrors.login.email ? 'error' : ''}`}
                value={formData.login.email}
                onChange={(e) => handleChange(e, 'login')}
                required
              />
              {formErrors.login.email && <div className="error-message">{formErrors.login.email}</div>}
            </div>
            <div className="auth-form-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                className={`auth-input ${formErrors.login.password ? 'error' : ''}`}
                value={formData.login.password}
                onChange={(e) => handleChange(e, 'login')}
                required
              />
              {formErrors.login.password && <div className="error-message">{formErrors.login.password}</div>}
            </div>
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div className="auth-form-group">
              <label>Name</label>
              <input
                type="text"
                name="name"
                className={`auth-input ${formErrors.register.name ? 'error' : ''}`}
                value={formData.register.name}
                onChange={(e) => handleChange(e, 'register')}
                required
              />
              {formErrors.register.name && <div className="error-message">{formErrors.register.name}</div>}
            </div>
            <div className="auth-form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                className={`auth-input ${formErrors.register.email ? 'error' : ''}`}
                value={formData.register.email}
                onChange={(e) => handleChange(e, 'register')}
                required
              />
              {formErrors.register.email && <div className="error-message">{formErrors.register.email}</div>}
            </div>
            <div className="auth-form-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                className={`auth-input ${formErrors.register.password ? 'error' : ''}`}
                value={formData.register.password}
                onChange={(e) => handleChange(e, 'register')}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                required
              />
              {formErrors.register.password && <div className="error-message">{formErrors.register.password}</div>}
              
              {/* Password strength meter */}
              {(passwordFocused || formData.register.password) && (
                <div className="password-strength-container">
                  <div className="password-strength-bar-container">
                    <div 
                      className="password-strength-bar" 
                      style={{ width: `${passwordStrength}%`, backgroundColor: strengthColor }}
                    ></div>
                  </div>
                  <div className="password-strength-label" style={{ color: strengthColor }}>
                    {strengthLabel}
                  </div>
                  
                  {/* Password requirements checklist */}
                  <div className="password-requirements">
                    <p className="requirements-title">Password must contain:</p>
                    <ul className="requirements-list">
                      {passwordRequirements.map((req, index) => (
                        <li 
                          key={index} 
                          className={req.test(formData.register.password) ? 'requirement-met' : 'requirement-missing'}
                        >
                          {req.test(formData.register.password) ? '✓' : '○'} {req.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
            <div className="auth-form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                className={`auth-input ${formErrors.register.confirmPassword ? 'error' : ''}`}
                value={formData.register.confirmPassword}
                onChange={(e) => handleChange(e, 'register')}
                required
              />
              {formErrors.register.confirmPassword && <div className="error-message">{formErrors.register.confirmPassword}</div>}
            </div>
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
